import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { v1 as uuid } from 'uuid'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET_LARGE } from 'env'
import { handleImageUpload } from 'lib/cloudinary/uploadImage'
import useStyles, {
  CardRoot, SectionTitle, HelperMsg, ErrorMsg, FacilityImage,
} from '../../../coaches-profile/profile.styles'

import TitleContentInput from 'components/title-content-input'
import TextFieldCounter from 'components/text-field-counter'
import FacilityDisplay from './FacilityDisplay'

const photosMsg = 'Add one photo per facility.'

const uploadOptions = {
  cloudName: CLOUDINARY_CLOUDNAME,
  upload_preset: CLOUDINARY_UPLOAD_PRESET_LARGE,
  sources: ['local', 'camera'],
  cropping: true,
  croppingAspectRatio: 1.7,
  showSkipCropButton: false,
  resourceType: 'image',
  multiple: false,
  folder: 'dot-com-images',
  showPoweredBy: false,
}

const CoachProfileEditFacilities = (props) => {
  const {
    formPrefix,
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    facilityIndex,
  } = props
  const styles = useStyles()

  const formField = name => (`${formPrefix}.${name}`)

  const addFacilityOffering = () => {
    // create uniq id for offering key for handling render
    const id = uuid()

    const newOffering = values.customData && values.customData.offerings
      ? [...values.customData.offerings, { id, name: '', description: '' }]
      : [{ id, name: '', description: '' }]

    setFieldValue(`${formField('customData.offerings')}`, newOffering)
  }

  const removeFacilityOffering = (idx) => {
    if (values.customData && values.customData.offerings) {
      setFieldValue(`${formField('customData.offerings')}`, values.customData.offerings.filter((_, i) => i !== idx))
    }
  }

  const handleCheckClick = (e) => {
    setFieldValue(e.target.name, e.target.checked)
  }

  const [uploadError, setUploadError] = useState()

  const facilityFormFields = (
    <FacilityDisplay
      values={values}
    />
  )

  return (
    <CardRoot>
      {facilityFormFields}
      <Grid item xs={12}>
        <Typography variant='h6'>
          Other Details (Optional)
        </Typography>
      </Grid>
      {values.isPrivate && (
        <FormControlLabel
          control={(
            <Checkbox
              id={`checkbox-public-coaching-${facilityIndex}`}
              name={formField('customData.offersCoachingAtPrivateClub')}
              checked={values.customData && values.customData.offersCoachingAtPrivateClub}
              onChange={handleCheckClick}
              color="secondary"
            />
          )}
          label="I offer coaching to the public at this facility"
          htmlFor={`checkbox-public-coaching-${facilityIndex}`}
          style={{ userSelect: 'none' }}
        />
      )}
      <TextFieldCounter
        characterLimit={1000}
        label="Facility Description"
        multiline
        rows={4}
        name={formField('customData.description')}
        onChange={handleChange}
        onBlur={handleBlur}
        type="text"
        variant="outlined"
        value={values.customData && values.customData.description}
        style={{ marginTop: '1rem' }}
        className={`multiline ${errors.customData && errors.customData.description && touched.customData && touched.customData.description && ' error'}`}
        error={touched.customData && touched.customData.description && errors.customData && errors.customData.description}
        helperText={touched.customData && touched.customData.description && errors.customData && errors.customData.description}
      />
      {/* Photos Sub-Section */}
      <SectionTitle style={{ marginTop: '1rem' }}>Photos</SectionTitle>
      <HelperMsg>{photosMsg}</HelperMsg>
      {
        (values.customData && values.customData.photos && values.customData.photos.length && values.customData.photos[0]) ? (
          <FacilityImage>
            <img src={values.customData.photos[0]} alt="Facility" />
          </FacilityImage>
        ) : null
      }
      <Box className={styles.flexRow} style={{ margin: '1rem 0 2rem' }}>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          type="button"
          onClick={() => handleImageUpload(uploadOptions, setFieldValue, setUploadError, `${formField('customData')}.photos`, [])}
        >
          Upload Photo
        </Button>
        <ErrorMsg>{uploadError}</ErrorMsg>
        {
          (values.customData && values.customData.photos && values.customData.photos.length) ? (
            <Button
              variant="text"
              color="primary"
              size="large"
              type="button"
              onClick={() => {
                setFieldValue(formField('customData.photos'), [])
              }}
            >
              Delete
            </Button>
          ) : null
        }
      </Box>
      <TitleContentInput
        addItem={addFacilityOffering}
        removeItem={removeFacilityOffering}
        headline="Facility Offerings"
        subtitle="What about your facility enhances your coaching ability?  Add up to 4 Facility Offerings"
        addButtonLabel="Add Facility Offering"
        titleLabel="Offering Name"
        contentLabel="Offering Description"
        maxItems={4}
        maxContentLength={200}
        fieldName={formField('customData.offerings')}
        values={values.customData && values.customData.offerings}
        errors={errors.customData && errors.customData.offerings}
        touched={touched.customData && touched.customData.offerings}
        onChange={handleChange}
      />
    </CardRoot>
  )
}

CoachProfileEditFacilities.propTypes = {
  values: PropTypes.shape({
    facilityId: PropTypes.string,
    pgaFacilityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    facilityName: PropTypes.string,
    customData: PropTypes.object,
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    phoneNumber: PropTypes.string,
    website: PropTypes.string,
    description: PropTypes.string,
    photos: PropTypes.array,
    offerings: PropTypes.array,
    facilityRank: PropTypes.number,
    isPrivate: PropTypes.bool,
    offersCoachingAtPrivateClub: PropTypes.bool,
  }),
  formPrefix: PropTypes.string,
  isPrimary: PropTypes.bool,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  setFieldValue: PropTypes.func,
}

CoachProfileEditFacilities.defaultProps = {
  values: {},
  errors: {},
  touched: {},
}

export default CoachProfileEditFacilities
